import { DropdownItem, TableRowValueList } from '@vinnova/ui';
import { Worksite } from '../../../../core/models/question/question-worksite.model';
import { Kostnadstyp } from '../budget.interface';
import {
  ExcelImportExcludedEntry,
  ExcelImportReconciliation,
  ExcelImportRowAssignments,
  ExcelImportSourceRow,
  ExcelImportSummary,
} from '../excel-import-modal/excel-import-modal.model';
import {
  EXCEL_IMPORT_SLOT_NONE,
  ExcelImportPlacedCell,
  placeExcelImportRows,
} from '../excel-import-modal/excel-import-row-placement';
import {
  UPPARBETADE_KOSTNADER_IMPORT_UNIT_FACTOR,
  UpparbetadeKostnaderImportApplyProjektpart,
  UpparbetadeKostnaderImportCell,
  UpparbetadeKostnaderImportDateGranularity,
  UpparbetadeKostnaderImportProjektpart,
  UpparbetadeKostnaderImportResult,
  UpparbetadeKostnaderImportUnit,
  UpparbetadeKostnaderSlotPeriod,
  UpparbetadeKostnaderSlotSchema,
} from './upparbetade-kostnader-import.interface';
import {
  DATE_GRANULARITY_LABELS,
  KOSTNADSSLAG_LABELS,
  LAYOUT_LABELS,
  UPPARBETADE_KOSTNADER_IMPORT_TEXT,
} from './upparbetade-kostnader-import.text';

export interface UpparbetadeKostnaderImportPreviewCell {
  ar: number;
  current: number;
  /** Scaled to SEK; undefined when the import had no value for this slot. */
  imported?: number;
  source?: string;
}

export interface UpparbetadeKostnaderImportPreviewRow {
  id: string;
  rowType: number;
  label: string;
  cells: UpparbetadeKostnaderImportPreviewCell[];
}

export interface UpparbetadeKostnaderImportPreviewPart {
  part: UpparbetadeKostnaderImportProjektpart;
  kostnader: UpparbetadeKostnaderImportPreviewRow[];
  hasImportedValues: boolean;
  sourceRows: ExcelImportSourceRow[];
  slotOptions: DropdownItem[];
}

export class UpparbetadeKostnaderImportHelper {
  static scale(summa: number, unit: UpparbetadeKostnaderImportUnit): number {
    return Math.round(
      summa * (UPPARBETADE_KOSTNADER_IMPORT_UNIT_FACTOR[unit] ?? 1),
    );
  }

  /**
   * Perioder carry exact dates (not just a year) so the backend can sum monthly/daily workbook columns correctly.
   * Koordinator is undefined when it has been inactivated and must not be part of the import.
   */
  static buildSchema(
    perioder: UpparbetadeKostnaderSlotPeriod[],
    koordinator: Worksite | undefined,
    projektparter: Worksite[],
  ): UpparbetadeKostnaderSlotSchema {
    const toPart = (
      worksite: Worksite,
      koordinerande: boolean,
    ): UpparbetadeKostnaderImportProjektpart => ({
      id: worksite.id,
      koordinerande,
      namn: worksite.company?.name ?? worksite.name,
      organisationsnummer: worksite.company?.nationalRegistrationNumber,
      adress: UpparbetadeKostnaderImportHelper.buildAdress(worksite),
    });

    return {
      perioder,
      projektparter: [
        ...(koordinator ? [toPart(koordinator, true)] : []),
        ...projektparter.map((p) => toPart(p, false)),
      ],
    };
  }

  /** Street, zip and city combined so a workbook caption naming only the city can still match. */
  private static buildAdress(worksite: Worksite): string | undefined {
    const street = worksite.address ?? worksite.company?.address;
    const zipcode = worksite.zipcode ?? worksite.company?.zipcode;
    const city = worksite.city ?? worksite.company?.city;
    return (
      [street, [zipcode, city].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', ') || undefined
    );
  }

  static buildPreview(
    result: UpparbetadeKostnaderImportResult,
    unit: UpparbetadeKostnaderImportUnit,
    current: Map<string, TableRowValueList>,
    assignments: ExcelImportRowAssignments = {},
  ): UpparbetadeKostnaderImportPreviewPart[] {
    const cellsByPart = new Map<string, UpparbetadeKostnaderImportCell[]>();
    for (const cell of result.cells) {
      const list = cellsByPart.get(cell.projektpartId) ?? [];
      list.push(cell);
      cellsByPart.set(cell.projektpartId, list);
    }
    const slotOptions = UpparbetadeKostnaderImportHelper.buildSlotOptions();

    return result.projektparter.map((part) => {
      const currentTable = current.get(part.id) ?? [];
      const { sourceRows, placedCells } = placeExcelImportRows(
        part.id,
        (cellsByPart.get(part.id) ?? []).map((cell) => ({
          ...cell,
          slot: cell.rowType?.toString() ?? '',
        })),
        slotOptions,
        assignments,
        result.years,
        (summa) => UpparbetadeKostnaderImportHelper.scale(summa, unit),
      );

      const kostnader = Object.values(Kostnadstyp).map((kostnadstyp) =>
        UpparbetadeKostnaderImportHelper.buildRow(
          Number(kostnadstyp),
          KOSTNADSSLAG_LABELS[kostnadstyp],
          result.years,
          placedCells,
          currentTable,
          unit,
        ),
      );

      return {
        part,
        kostnader,
        hasImportedValues: kostnader.some((row) =>
          row.cells.some((cell) => cell.imported !== undefined),
        ),
        sourceRows,
        slotOptions,
      };
    });
  }

  /** Where a file row can be placed: nothing, or one of the kostnadsslag. */
  private static buildSlotOptions(): DropdownItem[] {
    return [
      {
        value: EXCEL_IMPORT_SLOT_NONE,
        label: UPPARBETADE_KOSTNADER_IMPORT_TEXT.rowsIgnore,
      },
      ...Object.values(Kostnadstyp).map((kostnadstyp) => ({
        value: kostnadstyp,
        label: KOSTNADSSLAG_LABELS[kostnadstyp],
      })),
    ];
  }

  /** Turns confirmed preview parts into per-part patches, dropping cells the import left empty. */
  static toApplyParts(
    preview: Omit<
      UpparbetadeKostnaderImportPreviewPart,
      'sourceRows' | 'slotOptions'
    >[],
  ): UpparbetadeKostnaderImportApplyProjektpart[] {
    return preview.map((p) => {
      const kostnader: Record<string, Record<number, number>> = {};
      for (const row of p.kostnader) {
        for (const cell of row.cells) {
          if (cell.imported === undefined) {
            continue;
          }
          kostnader[row.id] ??= {};
          kostnader[row.id][cell.ar] = cell.imported;
        }
      }
      return { id: p.part.id, kostnader };
    });
  }

  /**
   * Overwrites only the cells present in the patch; other rows are untouched. Unlike the ansökan budget table,
   * this table's columnId is the plain year (e.g. "2027"), not a project-start-relative offset.
   */
  static mergeIntoTable(
    table: TableRowValueList,
    patch: UpparbetadeKostnaderImportApplyProjektpart,
  ): TableRowValueList {
    const merged = [...table];
    for (const [rowId, perYear] of Object.entries(patch.kostnader)) {
      for (const [year, value] of Object.entries(perYear)) {
        UpparbetadeKostnaderImportHelper.upsert(merged, rowId, year, value);
      }
    }
    return merged;
  }

  /** Undefined against a backend that does not preprocess, so the modal renders as before. */
  static buildSummary(
    result: UpparbetadeKostnaderImportResult,
  ): ExcelImportSummary | undefined {
    const p = result.preprocessing;
    if (!p) {
      return undefined;
    }
    return {
      layoutLabel: LAYOUT_LABELS[p.layout],
      dateDescription: UpparbetadeKostnaderImportHelper.describeDate(
        p.dateColumns,
        p.dateGranularity,
      ),
      rowsTotal: p.rowsTotal,
      rowsInPeriod: p.rowsInPeriod,
      rowsExcluded: p.rowsExcluded,
      sheetsSkipped: p.sheetsSkipped,
    };
  }

  /** "År + Månad (månad)", "År + Månad", "månad" or undefined. */
  private static describeDate(
    columns: string[] | undefined,
    granularity: UpparbetadeKostnaderImportDateGranularity | undefined,
  ): string | undefined {
    const columnText = columns?.length ? columns.join(' + ') : undefined;
    const granularityText = granularity
      ? DATE_GRANULARITY_LABELS[granularity]
      : undefined;
    if (columnText && granularityText) {
      return `${columnText} (${granularityText})`;
    }
    return columnText ?? granularityText;
  }

  static buildReconciliation(
    result: UpparbetadeKostnaderImportResult,
    partId: string,
    unit: UpparbetadeKostnaderImportUnit,
  ): ExcelImportReconciliation | undefined {
    const r = result.projektparter.find((p) => p.id === partId)?.reconciliation;
    if (!r) {
      return undefined;
    }
    const scale = (summa: number): number =>
      UpparbetadeKostnaderImportHelper.scale(summa, unit);
    return {
      totalInFile: scale(r.totalInFile),
      totalInPeriod: scale(r.totalInPeriod),
      totalExcluded: scale(r.totalExcludedOutsidePeriod),
      totalUnmatched: scale(r.totalUnmatchedCategory),
    };
  }

  static buildExcluded(
    result: UpparbetadeKostnaderImportResult,
    unit: UpparbetadeKostnaderImportUnit,
  ): ExcelImportExcludedEntry[] {
    return (result.excluded ?? []).map((e) => ({
      partId: e.projektpartId,
      source: `${e.sourceSheet}!${e.sourceRange}`,
      date: e.date,
      label: e.label,
      amount: UpparbetadeKostnaderImportHelper.scale(e.summa, unit),
      reason: e.reason,
    }));
  }

  private static buildRow(
    rowType: number,
    label: string,
    years: number[],
    cells: ExcelImportPlacedCell[],
    currentTable: TableRowValueList,
    unit: UpparbetadeKostnaderImportUnit,
  ): UpparbetadeKostnaderImportPreviewRow {
    return {
      id: rowType.toString(),
      rowType,
      label,
      cells: years.map((ar) => {
        const imported = cells.find(
          (c) => c.slot === rowType.toString() && c.ar === ar,
        );
        const current = currentTable.find(
          (c) => c.rowId === rowType.toString() && c.columnId === ar.toString(),
        );
        return {
          ar,
          current: Number(current?.value ?? 0),
          imported: imported
            ? UpparbetadeKostnaderImportHelper.scale(imported.summa, unit)
            : undefined,
          source: imported?.source,
        };
      }),
    };
  }

  private static upsert(
    table: TableRowValueList,
    rowId: string,
    columnId: string,
    value: number,
  ): void {
    const index = table.findIndex(
      (c) => c.rowId === rowId && c.columnId === columnId,
    );
    if (index === -1) {
      table.push({ rowId, columnId, value });
    } else {
      table[index] = { ...table[index], value };
    }
  }
}
